/**
 * Lightweight error reporter for the beta.
 *
 * - Hooks `window.error` and `unhandledrejection`.
 * - Provides `captureError()` for manual reports (used by ErrorBoundary).
 * - Batches and POSTs to the `report-error` edge function via sendBeacon
 *   when possible, falling back to fetch with `keepalive`.
 * - De-duplicates identical errors within a short window to avoid loops
 *   flooding the backend.
 * - No PII: only the error fields, current pathname, app version, and UA.
 */
import { supabase } from "@/integrations/supabase/client";
import { APP_VERSION, APP_COMMIT } from "@/lib/version";

type Source = "browser" | "boundary";

interface Report {
  source: Source;
  level?: "error" | "warning";
  message: string;
  name?: string;
  stack?: string;
  app_version: string;
  app_commit: string;
  path: string;
  user_agent: string;
  context?: Record<string, unknown>;
}

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/report-error`;
const APIKEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const QUEUE: Report[] = [];
const MAX_QUEUE = 20;
const FLUSH_DEBOUNCE_MS = 1000;
const DEDUPE_WINDOW_MS = 60_000;
const recentFingerprints = new Map<string, number>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let inited = false;

function shouldEnable(): boolean {
  if (typeof window === "undefined") return false;
  if (import.meta.env.DEV && import.meta.env.VITE_ERROR_REPORTING !== "1") {
    return false;
  }
  return true;
}

function fingerprint(r: Report): string {
  const firstFrame = (r.stack ?? "").split("\n")[1]?.trim() ?? "";
  return `${r.name ?? ""}|${r.message}|${firstFrame}|${r.path}`;
}

function pathOf(): string {
  try {
    return window.location.pathname + window.location.search;
  } catch {
    return "";
  }
}

function buildReport(
  source: Source,
  err: unknown,
  context?: Record<string, unknown>,
): Report {
  const isErr = err instanceof Error;
  return {
    source,
    level: "error",
    message: (isErr ? err.message : String(err) || "Unknown error").slice(0, 2000),
    name: (isErr ? err.name : "Error").slice(0, 200),
    stack: isErr && err.stack ? err.stack.slice(0, 8000) : undefined,
    app_version: APP_VERSION,
    app_commit: APP_COMMIT,
    path: pathOf().slice(0, 500),
    user_agent: navigator.userAgent.slice(0, 500),
    context,
  };
}

async function getAuthHeader(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return token ? `Bearer ${token}` : null;
  } catch {
    return null;
  }
}

async function send(reports: Report[]): Promise<void> {
  if (reports.length === 0) return;
  const body = JSON.stringify({ reports });
  const auth = await getAuthHeader();

  // sendBeacon doesn't support custom headers, so when we need to send
  // an Authorization header (logged-in users), use fetch+keepalive.
  if (!auth && typeof navigator !== "undefined" && "sendBeacon" in navigator) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(`${ENDPOINT}?apikey=${encodeURIComponent(APIKEY)}`, blob);
      if (ok) return;
    } catch {
      // fall through to fetch
    }
  }

  try {
    await fetch(ENDPOINT, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: APIKEY,
        ...(auth ? { Authorization: auth } : {}),
      },
      body,
    });
  } catch {
    // swallow — error reporting must never throw
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    const batch = QUEUE.splice(0, QUEUE.length);
    void send(batch);
  }, FLUSH_DEBOUNCE_MS);
}

function enqueue(report: Report) {
  // De-duplicate
  const fp = fingerprint(report);
  const now = Date.now();
  const last = recentFingerprints.get(fp);
  if (last && now - last < DEDUPE_WINDOW_MS) return;
  recentFingerprints.set(fp, now);
  // Trim dedupe map occasionally
  if (recentFingerprints.size > 200) {
    for (const [k, t] of recentFingerprints) {
      if (now - t > DEDUPE_WINDOW_MS) recentFingerprints.delete(k);
    }
  }

  QUEUE.push(report);
  if (QUEUE.length >= MAX_QUEUE) {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    const batch = QUEUE.splice(0, QUEUE.length);
    void send(batch);
  } else {
    scheduleFlush();
  }
}

export function captureError(
  err: unknown,
  opts: { source?: Source; context?: Record<string, unknown> } = {},
): void {
  if (!shouldEnable()) return;
  try {
    enqueue(buildReport(opts.source ?? "browser", err, opts.context));
  } catch {
    // never throw from the reporter
  }
}

export function initErrorReporter(): void {
  if (inited || !shouldEnable()) return;
  inited = true;

  window.addEventListener("error", (event) => {
    captureError(event.error ?? new Error(event.message), {
      source: "browser",
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    captureError(reason instanceof Error ? reason : new Error(String(reason)), {
      source: "browser",
      context: { kind: "unhandledrejection" },
    });
  });

  // Best-effort flush on page hide
  window.addEventListener("pagehide", () => {
    if (QUEUE.length > 0) {
      const batch = QUEUE.splice(0, QUEUE.length);
      void send(batch);
    }
  });
}
